import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ConfirmDeleteButton } from "@/components/admin/AdminWinesTab";

import { API } from "@/lib/apiBase";

const ROLES = ["all", "public", "member", "sommelier", "partner", "admin"];
const isProtected = (u, currentUserId) => u.id === currentUserId || u.role === "admin";

export default function AdminUsersTab({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users`);
      setUsers(r.data.users);
      setSelected(new Set());
    } catch (err) {
      console.error(err);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) =>
      (!q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)) &&
      (role === "all" || (u.role || "public") === role) &&
      (status === "all" || (status === "approved" ? u.approved : !u.approved))
    );
  }, [users, search, role, status]);

  const selectable = filtered.filter((u) => !isProtected(u, currentUserId));
  const selectedInView = selectable.filter((u) => selected.has(u.id));
  const allChecked = selectable.length > 0 && selectedInView.length === selectable.length;

  const toggleAll = (checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      selectable.forEach((u) => (checked ? next.add(u.id) : next.delete(u.id)));
      return next;
    });
  };

  const toggleOne = (id, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });
  };

  const bulkDelete = async () => {
    setDeleting(true);
    try {
      const r = await axios.post(`${API}/admin/users/bulk-delete`, { user_ids: selectedInView.map((u) => u.id) });
      const wines = r.data.wines_deleted ? ` and ${r.data.wines_deleted} submitted wine(s)` : "";
      toast.success(`Deleted ${r.data.deleted} user(s)${wines}`);
      setConfirmOpen(false);
      refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Bulk delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or email"
            data-testid="users-search"
            className="pl-9 bg-transparent border-white/10 text-white/90 font-body text-sm h-10"
          />
        </div>
        <FilterSelect value={role} onChange={setRole} testId="users-role-filter" options={ROLES.map((r) => [r, r === "all" ? "All roles" : r])} />
        <FilterSelect value={status} onChange={setStatus} testId="users-status-filter" options={[["all", "All statuses"], ["approved", "Approved"], ["pending", "Pending"]]} />
        <div className="flex items-center gap-3 lg:ml-auto">
          <span className="font-body text-xs text-white/40" data-testid="users-count">{filtered.length} of {users.length} users</span>
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={selectedInView.length === 0}
            data-testid="users-bulk-delete-btn"
            className="bg-merlot hover:bg-merlot-light text-white gap-2 h-10 font-body text-sm disabled:opacity-40"
          >
            <Trash2 className="w-4 h-4" /> Delete selected ({selectedInView.length})
          </Button>
        </div>
      </div>

      <div className="glass-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="w-10">
                <Checkbox checked={allChecked} onCheckedChange={(c) => toggleAll(!!c)} disabled={selectable.length === 0} data-testid="users-select-all" aria-label="Select all" className="border-white/30" />
              </TableHead>
              {["Name", "Email", "Role", "Status", "Created"].map((c) => (
                <TableHead key={c} className="text-white/40 font-body text-xs uppercase tracking-wider">{c}</TableHead>
              ))}
              <TableHead className="text-white/40 font-body text-xs uppercase tracking-wider text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <EmptyRow text="Loading..." />
            ) : filtered.length === 0 ? (
              <EmptyRow text="No users match these filters" />
            ) : (
              filtered.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  isSelf={u.id === currentUserId}
                  locked={isProtected(u, currentUserId)}
                  checked={selected.has(u.id)}
                  onCheck={(c) => toggleOne(u.id, c)}
                  onChange={refresh}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-wine-paper border-white/10" data-testid="users-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white/90 font-display">Delete {selectedInView.length} user(s)?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50 font-body">
              This permanently removes these accounts and everything they own: favorites, sommelier ratings and every wine they submitted (including approved catalog wines). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-40 overflow-y-auto text-xs font-body text-white/60 space-y-1" data-testid="users-bulk-delete-list">
            {selectedInView.slice(0, 50).map((u) => <p key={u.id}>{u.email}</p>)}
            {selectedInView.length > 50 && <p className="text-white/40">…and {selectedInView.length - 50} more</p>}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 text-white/70 bg-transparent" data-testid="users-bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); bulkDelete(); }}
              disabled={deleting}
              data-testid="users-bulk-delete-confirm"
              className="bg-merlot hover:bg-merlot-light"
            >
              {deleting ? "Deleting…" : `Delete ${selectedInView.length}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FilterSelect({ value, onChange, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="w-full lg:w-40 bg-transparent border-white/10 text-white/80 h-10 font-body text-sm capitalize">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-wine-paper border-white/10">
        {options.map(([v, label]) => (
          <SelectItem key={v} value={v} className="capitalize">{label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function EmptyRow({ text }) {
  return (
    <TableRow>
      <TableCell colSpan={7} className="text-white/40 font-body py-8 text-center">{text}</TableCell>
    </TableRow>
  );
}

function UserRow({ user, isSelf, locked, checked, onCheck, onChange }) {
  const remove = async () => {
    try {
      await axios.delete(`${API}/admin/users/${user.id}`);
      toast.success(`Deleted ${user.email}`);
      onChange();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Delete failed");
    }
  };

  return (
    <TableRow className={`border-white/5 hover:bg-white/[0.02] ${checked ? "bg-merlot/[0.08]" : ""}`} data-testid={`user-row-${user.id}`}>
      <TableCell>
        <Checkbox checked={checked} onCheckedChange={(c) => onCheck(!!c)} disabled={locked} data-testid={`user-select-${user.id}`} aria-label={`Select ${user.email}`} className="border-white/30" />
      </TableCell>
      <TableCell className="text-white/85 font-body text-sm">
        {user.name}
        {isSelf && <Badge variant="outline" className="ml-2 text-[10px] border-gold/40 text-gold">YOU</Badge>}
      </TableCell>
      <TableCell className="text-white/60 font-body text-sm">{user.email}</TableCell>
      <TableCell>
        <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-white/15 text-white/60">
          {user.role || "public"}
        </Badge>
      </TableCell>
      <TableCell>
        {user.approved ? (
          <Badge variant="outline" className="text-[10px] border-emerald-400/40 text-emerald-400">APPROVED</Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] border-gold/40 text-gold">PENDING</Badge>
        )}
      </TableCell>
      <TableCell className="text-white/40 font-body text-xs">
        {user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
      </TableCell>
      <TableCell className="text-right">
        <ConfirmDeleteButton
          disabled={locked}
          testId={`delete-user-${user.id}`}
          confirmTestId={`confirm-delete-user-${user.id}`}
          title="Delete user?"
          description={`"${user.email}" will be permanently removed along with their favorites, ratings and submitted wines.`}
          onConfirm={remove}
        />
      </TableCell>
    </TableRow>
  );
}
